import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import Header from "../components/Header";
import Button from "../components/Button";
import UserMenu from "../components/UserMenu";
import { getUser, removeUser } from "../utils/auth";
import { savePenToStorage, clearPenStorage } from "../utils/penStorage";

const API = '/api';

export default function SharedPenPage() {
  const navigate          = useNavigate();
  const location          = useLocation();
  const [user, setUser]   = useState(getUser);
  const [pen, setPen]     = useState(null);
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    removeUser();
    setUser(null);
    navigate('/');
  };

  const handleNewPen = () => {
    clearPenStorage();
    navigate('/editor');
  };

  useEffect(() => {
    setLoading(true);

    // Check ?pen= URL param first (from share link) — an explicit token
    // always wins over a stale pen left in sessionStorage from an earlier visit.
    const params   = new URLSearchParams(location.search);
    const penToken = params.get('pen');
    if (penToken) {
      fetch(`${API}/get_shared_pen.php?token=${penToken}`)
        .then(r => r.json())
        .then(data => { if (!data.error) setPen(data); setLoading(false); })
        .catch(() => setLoading(false));
      return;
    }

    // Fall back to sessionStorage (from Explore page) — consume it once so it
    // doesn't leak into a later /shared visit that has no token of its own.
    const stored = sessionStorage.getItem('wsncode_shared_pen');
    if (stored) {
      setPen(JSON.parse(stored));
      sessionStorage.removeItem('wsncode_shared_pen');
      setLoading(false);
      return;
    }

    setLoading(false);
  }, [location.search]);

  const handleFork = () => {
    if (!pen) return;
    savePenToStorage({
      id: null,
      title: 'Fork of ' + (pen.title || 'Untitled'),
      html: pen.html || '',
      css:  pen.css  || '',
      js:   pen.js   || '',
    });
    navigate('/editor');
  };

  if (loading) return (
    <div style={{background:'#1e1e1e', color:'#fff', minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px'}}>
      Loading...
    </div>
  );

  if (!pen) return (
    <div style={{background:'#1e1e1e', color:'#fff', minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'16px'}}>
      <p>Pen not found.</p>
      <Button variant="primary" onClick={() => navigate('/')}>Go Home</Button>
    </div>
  );

  return (
    <div className="shared-page">
      <Header
        onLogoClick={() => navigate('/')}
        leftExtra={
          <div className="shared-pen-info">
            <span className="shared-pen-title">{pen.title || 'Untitled'}</span>
            <span className="shared-pen-author">by {pen.author_name || 'Anonymous'}</span>
          </div>
        }
        rightExtra={
          user ? (
            <>
              <Button variant="primary" onClick={handleFork}>🍴 Fork</Button>
              <Button variant="ghost" onClick={() => navigate('/editor')}>✏️ Editor</Button>
            </>
          ) : (
            <>
              <Button variant="primary" onClick={() => navigate('/login')}>🔑 Login to Fork</Button>
              <Button variant="secondary" onClick={() => navigate('/')}>Home</Button>
            </>
          )
        }
        authSlot={
          user ? (
            <UserMenu
              user={user}
              onLogout={handleLogout}
              items={[
                { label: 'New Pen',  icon: '✏️', onClick: handleNewPen },
                { label: 'Home',     icon: '🏠', onClick: () => navigate('/') },
                { label: 'My Pens',  icon: '📁', onClick: () => navigate('/editor?pens=true') },
                { label: 'Explore',  icon: '🔥', onClick: () => navigate('/explore') },
                { label: 'Settings', icon: '⚙️', onClick: () => navigate('/editor?settings=true') },
              ]}
            />
          ) : null
        }
      />

      <div className="shared-editors">
        <div className="shared-editor-panel">
          <div className="shared-editor-label html-label">● HTML</div>
          <div className="shared-code-view">{pen.html}</div>
        </div>
        <div className="shared-editor-panel">
          <div className="shared-editor-label css-label">● CSS</div>
          <div className="shared-code-view">{pen.css}</div>
        </div>
        <div className="shared-editor-panel">
          <div className="shared-editor-label js-label">● JS</div>
          <div className="shared-code-view">{pen.js}</div>
        </div>
      </div>

      <div className="shared-preview">
        <div className="shared-preview-label">PREVIEW</div>
        <iframe
          title="shared-preview"
          sandbox="allow-scripts"
          srcDoc={`<!DOCTYPE html><html><head><style>${pen.css||''}</style></head><body>${pen.html||''}<script>${pen.js||''}</script></body></html>`}
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      </div>
    </div>
  );
}